import { clients } from 'lib/https';
import { ConfigurationSettings } from '/models/configuration-settings';
import { PurchaseChannel } from 'constant/plan';
import { PendingQuantityChange, SubscriptionQuantity } from 'state/store/subscription/subscriptionStore';

export interface GetPlansFilter {
  name?: string;
  title?: string;
}

export interface GetPlansSort {
  property: string;
  isDescending: boolean;
}

export interface GetPlansRequest {
  page: number;
  pageSize: number;
  filter?: GetPlansFilter;
  sort?: GetPlansSort;
}

export interface BillingAddress {
  itemId: string;
  email: string;
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  vat: string;
  organization: {
    itemId: string | null;
    name: string | null;
  } | null;
}

export interface BillingAddressRequest {
  email: string;
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  vat: string | null;
}

export interface Organization {
  email: string;
  name: string;
  address: string;
}

export interface UpdateBillingAddressRequest {
  itemId: string;
  email: string;
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  vat: string;
}

export interface UpdateBillingAddressResponse {
  itemId: string;
  errors: string | null;
  isSuccess: boolean;
}

export interface GenerateCheckoutLinkRequest {
  PlanId: string;
  PlanItemId: string;
  BillingAddress: BillingAddressRequest;
  Organization?: Organization;
  PromoCode?: string | null;
}

export interface GenerateCheckoutLinkResponse {
  link: string;
  errors?: string;
  isSuccess: boolean;
}

/**
 * Generates a checkout link for the subscription.
 *
 * @param {GenerateCheckoutLinkRequest} data - The checkout link generation data.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<GenerateCheckoutLinkResponse>} The response containing the checkout URL.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await generateCheckoutLink(checkoutData, config);
 */
export const generateCheckoutLink = async (
  data: GenerateCheckoutLinkRequest,
  config: ConfigurationSettings
): Promise<GenerateCheckoutLinkResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.generateCheckoutLink}`;

  return clients.post<GenerateCheckoutLinkResponse>(
    url,
    config.blocksKey,
    JSON.stringify({
      ...data,
      ProjectKey: config.blocksKey
    })
  );
};

export interface GetBillingAddressResponse {
  data: BillingAddress | null;
  errors: string | null;
}

export const getBillingAddress = async (
  config: ConfigurationSettings
): Promise<GetBillingAddressResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.getBillingAddress}`;

  return clients.get<GetBillingAddressResponse>(url, config.blocksKey);
};

/**
 * Updates the billing address for the user.
 *
 * @param {UpdateBillingAddressRequest} data - The billing address update data.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpdateBillingAddressResponse>} The response containing the update result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateBillingAddress(updateData, config);
 */
export const updateBillingAddress = async (
  data: UpdateBillingAddressRequest,
  config: ConfigurationSettings
): Promise<UpdateBillingAddressResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.updateBillingAddress}`;

  return clients.post<UpdateBillingAddressResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SubscribeFreePlanRequest {
  // No request payload needed for free plan subscription
}

export interface SubscribeFreePlanResponse {
  isSuccess: boolean;
  itemId: string;
  errors: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CancelSubscriptionRequest {
  // No request payload needed for cancellation
}

export interface CancelSubscriptionResponse {
  isSuccess: boolean;
  itemId: string;
  errors: string | null;
}

/**
 * Subscribes a user to the free plan.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<SubscribeFreePlanResponse>} The response containing the subscription result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await subscribeFreePlan(config);
 */
export const subscribeFreePlan = async (
  config: ConfigurationSettings
): Promise<SubscribeFreePlanResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.subscribeFreePlan}`;

  return clients.post<SubscribeFreePlanResponse>(
    url,
    config.blocksKey,
    JSON.stringify({})
  );
};

/**
 * Cancels a user's subscription.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<CancelSubscriptionResponse>} The response containing the cancellation result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await cancelSubscription(config);
 */
export const cancelSubscription = async (
  config: ConfigurationSettings
): Promise<CancelSubscriptionResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.cancelSubscription}`;

  return clients.post<CancelSubscriptionResponse>(
    url,
    config.blocksKey,
    JSON.stringify({})
  );
};

export interface UpdateSubscriptionRequest {
  PlanId: string;
  PlanItemId: string | undefined;
  Organization?: Organization;
}

export interface UpdateSubscriptionResponse {
  isSuccess: boolean;
  link: string;
  errors: string | null;
}

export interface UpgradeSubscriptionForUserRequest {
  userId: string;
  planId: string;
  planItemId: string;
}

export interface UpgradeSubscriptionForUserResponse {
  itemId: string;
  errors: {
    message: string;
  } | null;
  isSuccess: boolean;
}

export interface CancelAllUserSubscriptionsRequest {
  userId: string;
}

export interface CancelAllUserSubscriptionsResponse {
  itemId: string;
  errors: {
    message: string;
  } | null;
  isSuccess: boolean;
}

export interface GetCouponInfoRequest {
  promoCode: string;
  planId: string;
  planItemId: string;
}

export interface GetCouponInfoResponse {
  planPrice: number;
  totalDiscount: number;
  couponDiscount: number;
  subTotalPrice: number;
  totalTax: number;
  totalPrice: number;
  totalDiscountPercent: number;
  couponDiscountPercent: number;
  errors: {
    message: string;
  } | null;
  isSuccess: boolean;
}

export interface GetPlanUpdatePriceRequest {
  PlanId: string;
  PlanItemId: string;
}

export interface GetPlanUpdatePriceResponse {
  currentPlanRemainingAmount: number;
  newPlanRequiredAmount: number;
  subTotalPrice: number;
  totalTax: number;
  totalPrice: number;
  totalDiscount: number;
  totalDiscountPercent: number;
  planCycleStartDate: string;
  planCycleEndDate: string;
  errors: string | null;
  isSuccess: boolean;
}

/**
 * Updates the user's subscription to a new plan.
 *
 * @param {UpdateSubscriptionRequest} data - The update subscription data.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpdateSubscriptionResponse>} The response containing the update result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateSubscription(updateData, config);
 */
export const updateSubscription = async (
  data: UpdateSubscriptionRequest,
  config: ConfigurationSettings
): Promise<UpdateSubscriptionResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.updateSubscription}`;

  return clients.post<UpdateSubscriptionResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

/**
 * Upgrades a user's subscription plan (admin function).
 *
 * @param {UpgradeSubscriptionForUserRequest} data - The upgrade subscription data containing userId, planId, and planItemId.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpgradeSubscriptionForUserResponse>} The response containing the upgrade result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await upgradeSubscriptionForUser({
 *   userId: "f705bbda-82b1-4fce-bd3f-df7ef2a0c1ee",
 *   planId: "c8734548-1d22-4ba5-b4da-1161f1de6cd0",
 *   planItemId: "0fc02707-2006-45ed-98e8-e0079b291758"
 * }, config);
 */
export const upgradeSubscriptionForUser = async (
  data: UpgradeSubscriptionForUserRequest,
  config: ConfigurationSettings
): Promise<UpgradeSubscriptionForUserResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.upgradeSubscriptionPlanForUser}`;

  return clients.post<UpgradeSubscriptionForUserResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

/**
 * Cancels all subscriptions for a specific user (admin function).
 *
 * @param {CancelAllUserSubscriptionsRequest} data - The request data containing userId.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<CancelAllUserSubscriptionsResponse>} The response containing the cancellation result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await cancelAllUserSubscriptions({
 *   userId: "f705bbda-82b1-4fce-bd3f-df7ef2a0c1ee"
 * }, config);
 */
export const cancelAllUserSubscriptions = async (
  data: CancelAllUserSubscriptionsRequest,
  config: ConfigurationSettings
): Promise<CancelAllUserSubscriptionsResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.cancelAllUserSubscriptions}`;

  return clients.post<CancelAllUserSubscriptionsResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

/**
 * Gets coupon information for a specific promo code and plan.
 *
 * @param {GetCouponInfoRequest} data - The coupon info request data containing promoCode and planId.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<GetCouponInfoResponse>} The response containing the coupon discount information.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await getCouponInfo({
 *   promoCode: "EAGLE2027",
 *   planId: "c8734548-1d22-4ba5-b4da-1161f1de6cd0"
 * }, config);
 */
export const getCouponInfo = async (
  data: GetCouponInfoRequest,
  config: ConfigurationSettings
): Promise<GetCouponInfoResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.getCouponInfo}`;

  return clients.post<GetCouponInfoResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

/**
 * Gets the updated price for a plan change/upgrade.
 *
 * @param {GetPlanUpdatePriceRequest} data - The plan update price request data containing PlanId and PlanItemId.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<GetPlanUpdatePriceResponse>} The response containing the updated price information.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await getPlanUpdatePrice({
 *   PlanId: "1f6a43a6-d2db-4343-9e2c-bceb9a5e1279",
 *   PlanItemId: "94cf0ef7-a5ee-4116-8fdd-b9e06ffeb503"
 * }, config);
 */
export const getPlanUpdatePrice = async (
  data: GetPlanUpdatePriceRequest,
  config: ConfigurationSettings
): Promise<GetPlanUpdatePriceResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.getPlanUpdatePrice}`;

  return clients.post<GetPlanUpdatePriceResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};


export interface QuantityItem {
  itemKey: string;
  quantity: number;
}

export interface PreviewQuantityChangeRequest {
  version: number;
  quantities: QuantityItem[];
}

export interface PreviewQuantityChangeResponse {
  subscriptionId: string;
  version: number;
  preview: boolean;
  timing: 'Immediate' | 'NextRenewal';
  effectiveAtUtc: string;
  quantities: SubscriptionQuantity[];
  currentTier: unknown | null;
  targetTier: unknown | null;
  proratedChargeMinor: number;
  nextRenewalAmountMinor: number;
  effectiveUnitAmountMinor: number;
  taxAmountMinor: number;
  netAmountMinor: number;
  creditConsumedMinor: number;
  taxRateBasisPoints: number | null;
  taxMode: string | null;
  automaticDiscountBasisPoints: number | null;
  quantityDiscountCombination: string | null;
  grossAmountMinor: number;
  builtInDiscountMinor: number;
  promotionalDiscountMinor: number;
  discountedAmountMinor: number;
  promotionApplied: boolean;
  currencyCode: string;
  chargePaymentDetailId: string | null;
  pendingQuantityChange: PendingQuantityChange | null;
}

interface QuantityChangeEnvelope {
  success: boolean;
  data: PreviewQuantityChangeResponse | null;
  error: string | null;
  meta?: {
    correlationId: string;
    timestampUtc: string;
    replayed: boolean;
  };
}

export interface UpdateQuantityRequest {
  version: number;
  quantities: QuantityItem[];
}

export type UpdateQuantityResponse = PreviewQuantityChangeResponse;

/**
 * Previews the cost of changing subscription quantity (e.g., team seats).
 * Shows prorated charge for immediate change and next renewal amount.
 *
 * @param {string} subscriptionId - The subscription ID to preview changes for.
 * @param {PreviewQuantityChangeRequest} data - The preview request data with version and new quantities.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<PreviewQuantityChangeResponse>} The response containing proration details.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const preview = await previewQuantityChange('sub-123', {
 *   version: 7,
 *   quantities: [{ itemKey: 'user', quantity: 60 }]
 * }, config);
 */
export const previewQuantityChange = async (
  subscriptionId: string,
  data: PreviewQuantityChangeRequest,
  config: ConfigurationSettings
): Promise<PreviewQuantityChangeResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.previewQuantityChange.replace('{subscriptionId}', subscriptionId)}`;

  const res = await clients.post<QuantityChangeEnvelope>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );

  if (!res?.success || !res.data) {
    throw new Error(res?.error || 'Failed to preview quantity change');
  }

  return res.data;
};

/**
 * Updates the subscription quantity (e.g., team seats).
 * Charges prorated amount immediately and updates next renewal.
 *
 * @param {string} subscriptionId - The subscription ID to update.
 * @param {UpdateQuantityRequest} data - The update request data with version and new quantities.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpdateQuantityResponse>} The response containing the update result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const result = await updateQuantity('sub-123', {
 *   version: 7,
 *   quantities: [{ itemKey: 'user', quantity: 60 }]
 * }, config);
 */
export const updateQuantity = async (
  subscriptionId: string,
  data: UpdateQuantityRequest,
  config: ConfigurationSettings
): Promise<UpdateQuantityResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.updateQuantity.replace('{subscriptionId}', subscriptionId)}`;

  const res = await clients.put<QuantityChangeEnvelope>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );

  if (!res?.success || !res.data) {
    throw new Error(res?.error || 'Failed to update seats');
  }

  return res.data;
};

export interface BillingProfileAddress {
  line1: string;
  line2?: string | null;
  city: string;
  region?: string | null;
  postalCode: string;
  countryCode: string;
}

export interface BillingProfile {
  organizationId: string;
  legalName: string;
  displayName: string;
  billingContactName: string;
  billingContactEmail: string;
  address: BillingProfileAddress | null;
  taxRegistrationId: string | null;
  isComplete: boolean;
  missingFields: string[];
  lastUpdatedDateUtc: string;
}

export interface UpdateBillingProfileRequest {
  legalName: string;
  displayName: string;
  billingContactName: string;
  billingContactEmail: string;
  address?: BillingProfileAddress;
  taxRegistrationId?: string | null;
  organizationId: string;
}

export type UpdateBillingProfileResponse = BillingProfile;

interface BillingProfileEnvelope {
  success: boolean;
  data: BillingProfile | null;
  error: string | null;
  meta?: {
    correlationId: string;
    timestampUtc: string;
    replayed: boolean;
  };
}

export interface BillingProfileFormValues {
  legalName: string;
  displayName?: string;
  billingContactName: string;
  billingContactEmail: string;
  line1?: string;
  line2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  countryCode?: string;
  taxRegistrationId?: string;
}

export const buildUpdateBillingProfileRequest = (
  organizationId: string,
  form: BillingProfileFormValues
): UpdateBillingProfileRequest => {
  const line2 = form.line2?.trim();
  const region = form.region?.trim();
  const taxRegistrationId = form.taxRegistrationId?.trim();

  const line1 = form.line1?.trim();
  const city = form.city?.trim();
  const postalCode = form.postalCode?.trim();
  const countryCode = form.countryCode?.trim().toUpperCase();
  const address =
    line1 && city && postalCode && countryCode
      ? {
          line1,
          ...(line2 ? { line2 } : {}),
          city,
          ...(region ? { region } : {}),
          postalCode,
          countryCode,
        }
      : undefined;

  return {
    legalName: form.legalName.trim(),
    displayName: form.displayName?.trim() || form.legalName.trim(),
    billingContactName: form.billingContactName.trim(),
    billingContactEmail: form.billingContactEmail.trim(),
    ...(address && { address }),
    ...(taxRegistrationId ? { taxRegistrationId } : {}),
    organizationId,
  };
};

/**
 * Gets the organization subscription billing profile.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<BillingProfile | null>} The billing profile, or null when none exists.
 *
 * @throws {Error} If the request fails or the server returns an error.
 */
export const getBillingProfile = async (
  config: ConfigurationSettings
): Promise<BillingProfile | null> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.subscriptionBillingProfile}`;

  const res = await clients.get<BillingProfileEnvelope>(url, config.blocksKey);

  if (!res?.success) {
    throw new Error(res?.error || 'Failed to get billing profile');
  }

  return res.data ?? null;
};

/**
 * Creates or updates the organization subscription billing profile.
 *
 * @param {UpdateBillingProfileRequest} data - The billing profile update payload.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpdateBillingProfileResponse>} The updated billing profile.
 *
 * @throws {Error} If the request fails or the server returns an error.
 */
export const updateBillingProfile = async (
  data: UpdateBillingProfileRequest,
  config: ConfigurationSettings
): Promise<UpdateBillingProfileResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.subscriptionBillingProfile}`;

  const res = await clients.put<BillingProfileEnvelope>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );

  if (!res?.success || !res.data) {
    throw new Error(res?.error || 'Failed to update billing profile');
  }

  return res.data;
};

export interface DiscountPreviewQuantity {
  itemKey: string;
  quantity: number;
}

export interface DiscountPreviewRequest {
  planCode: string;
  priceId: string;
  quantities?: DiscountPreviewQuantity[];
  timeZoneId: string;
  discountCode: string;
  organizationId?: string;
  billingEmail: string;
  billingName: string;
}

export interface DiscountPreviewChargeBreakdown {
  subtotalMinor: number;
  builtInDiscountMinor: number;
  promotionalDiscountMinor: number;
  discountMinor: number;
  netSubtotalMinor: number;
  tax: number | null;
  totalMinor: number;
}

export interface DiscountPreviewNextRenewal extends DiscountPreviewChargeBreakdown {
  renewalAtUtc: string;
}

export interface DiscountPreviewNextCharge extends DiscountPreviewChargeBreakdown {
  chargeAtUtc: string;
  periodStartUtc: string;
  periodEndUtc: string;
  prorated: boolean;
  coveredDays: number | null;
  totalDays: number | null;
}

export interface DiscountPreviewQuote {
  currencyCode: string;
  subtotalMinor: number;
  discountMinor: number;
  builtInDiscountMinor: number;
  promotionalDiscountMinor: number;
  taxMinor: number;
  netSubtotalMinor: number;
  tax: number | null;
  totalDueNowMinor: number;
  prorated: boolean;
  coveredDays: number | null;
  totalDays: number | null;
  periodStartUtc: string;
  periodEndUtc: string;
  nextRenewalAtUtc: string;
  nextRenewalAmountMinor: number;
  nextRenewal: DiscountPreviewNextRenewal;
  nextCharge: DiscountPreviewNextCharge;
  trialEndsAtUtc: string | null;
  requiresCardSetup: boolean;
  blockers: string[];
  quotedAtUtc: string;
  quoteValidUntilUtc: string | null;
}

export type DiscountPreviewStatus =
  | 'Applied'
  | 'Invalid'
  | 'Expired'
  | 'NotApplicable'
  | 'AlreadyRedeemed'
  | string;

export interface DiscountPreviewResult {
  status: DiscountPreviewStatus;
  reasonCode: string | null;
  message: string | null;
  quote: DiscountPreviewQuote;
}

interface DiscountPreviewEnvelope {
  success: boolean;
  data: DiscountPreviewResult | null;
  error: string | null;
  meta?: {
    correlationId: string;
    timestampUtc: string;
    replayed: boolean;
  };
}

/**
 * A single metered entitlement for the organization's current billing period,
 * as returned by the subscription-usage endpoint (e.g. the `ai-credits` meter).
 *
 * The quantity fields (`included`, `used`, `remaining`, `overage`, `projection`)
 * are already expressed in display units — `unitLabel` names that unit and
 * `quantityScale` is the decimal precision the meter tracks internally, not a
 * divisor to apply here.
 */
export interface SubscriptionUsageMeter {
  allowed: boolean;
  /** Empty for the organisation's own meter, else the user the meter belongs to. */
  userId: string;
  meterKey: string;
  unitLabel: string;
  periodKey: string;
  periodStartUtc: string;
  periodEndUtc: string;
  quantityScale: number;
  included: number;
  used: number;
  remaining: number;
  overage: number;
  replayed: boolean;
  projection: number;
}

interface SubscriptionUsageEnvelope {
  success: boolean;
  data: SubscriptionUsageMeter[] | null;
  error: string | null;
  meta?: {
    correlationId: string;
    timestampUtc: string;
    replayed: boolean;
  };
}

/**
 * Gets the organization's AI-credit (and any other metered) usage for the
 * current billing period. This is organization-wide, not per-user.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<SubscriptionUsageMeter[]>} The list of usage meters, or an empty array when none exist.
 *
 * @throws {Error} If the request fails or the server returns an error.
 */
export const getSubscriptionUsage = async (
  config: ConfigurationSettings
): Promise<SubscriptionUsageMeter[]> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.subscriptionUsage}`;

  const res = await clients.get<SubscriptionUsageEnvelope>(url, config.blocksKey);

  if (!res?.success) {
    throw new Error(res?.error || 'Failed to get subscription usage');
  }

  return res.data ?? [];
};

/**
 * Previews a discount/coupon code against a plan quote without redeeming it.
 *
 * @param {DiscountPreviewRequest} data - The plan, price and discount code to preview.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<DiscountPreviewResult>} The discount status and resulting quote.
 *
 * @throws {Error} If the request fails or the server returns an error.
 */
export const previewSubscriptionDiscount = async (
  data: DiscountPreviewRequest,
  config: ConfigurationSettings
): Promise<DiscountPreviewResult> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.subscriptionDiscountPreview}`;

  const res = await clients.post<DiscountPreviewEnvelope>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );

  if (!res?.success || !res.data) {
    throw new Error(res?.error || 'Failed to preview discount');
  }

  return res.data;
};

